import { Col, Divider, Row, Tag, Tooltip, Typography } from 'antd';
import { IUser } from 'interfaces/users';
import { InfoCircleOutlined } from '@ant-design/icons';
import { ShowFieldData } from 'components/ShowFieldData';
import { getTenant } from 'services/tenants';
import { formatCPF } from 'utils/document-formatters';
import { useCan, useList } from '@refinedev/core';
import { useMemo } from 'react';

interface UserDetailsProps {
  userData: IUser;
}

const InfoField = ({
  label,
  value
}: {
  label: React.ReactNode;
  value?: string | null;
}) => (
  <Col xs={24} sm={12} md={8}>
    <ShowFieldData label={label} value={value ?? '--'} />
  </Col>
);

export const UserDetails = ({ userData }: UserDetailsProps) => {
  const canListStates = useCan({ resource: 'states', action: 'list' });
  const stateId = userData?.address?.city?.state_id;

  const { data: states } = useList({
    resource: 'states',
    queryOptions: {
      enabled: Boolean(stateId && canListStates.data?.can)
    },
    pagination: {
      pageSize: 30
    }
  });

  const stateUf = useMemo(() => {
    if (!stateId) return '--';
    return states?.data?.find(state => state.id === stateId)?.uf ?? '--';
  }, [stateId, states]);

  const hasAddress = Boolean(
    userData?.address?.postal_code ||
      userData?.address?.street ||
      userData?.address?.neighborhood ||
      userData?.address?.address_number ||
      stateId
  );

  return (
    <div className="pt-4">
      <Row gutter={[16, 24]}>
        <InfoField
          label={
            <span className="flex items-center gap-1">
              Nome completo
              <Tooltip title="Utilizado para emissão de certificados. Nome conforme documento oficial.">
                <InfoCircleOutlined className="text-blue-400" />
              </Tooltip>
            </span>
          }
          value={userData?.personal_info?.full_name}
        />
        <InfoField
          label="Como deseja ser chamado(a)"
          value={userData?.name}
        />
        <InfoField
          label="CPF"
          value={formatCPF(userData?.personal_info?.document_info ?? '')}
        />
        <InfoField label="E-mail" value={userData?.email} />
        <InfoField
          label="Telefone"
          value={userData?.personal_info?.phone_number}
        />
        {getTenant().name === 'estacio' && (
          <InfoField
            label="ID externo"
            value={userData?.estacio_id ?? 'Não informado'}
          />
        )}
      </Row>

      <Divider />

      <div className="flex items-center gap-2 mb-4">
        <Typography.Title level={5} className="!mb-0">
          Endereço
        </Typography.Title>
        {!hasAddress && (
          <Tag color="default">Não informado</Tag>
        )}
      </div>
      <Row gutter={[16, 24]}>
        <InfoField label="CEP" value={userData?.address?.postal_code} />
        <InfoField label="Endereço" value={userData?.address?.street} />
        <InfoField label="Número" value={userData?.address?.address_number} />
        <InfoField label="Complemento" value={userData?.address?.address_complement} />
        <InfoField label="Bairro" value={userData?.address?.neighborhood} />
        <InfoField label="Estado" value={stateUf} />
        <InfoField label="Cidade" value={userData?.address?.city?.name} />
      </Row>
    </div>
  );
};
